"use client";

import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

// A simple, abstract logo component, inspired by the reference
const DashspaceLogo = () => <div className="w-3 h-3 bg-black rounded-full" />;

export default function DashspaceComingSoonPage() {
  const [email, setEmail] = useState("");

  return (
    <div className="bg-white text-zinc-800 font-sans antialiased">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Top Nav - subtle and minimal */}
        <header className="flex justify-between items-center py-4 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <DashspaceLogo />
            <span>Dashspace</span>
          </div>
          <a href="/" className="group inline-flex items-center">
            We're hiring
            <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </header>

        <main className="py-20 md:py-28">
          {/* Header Section */}
          <section className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col">
              <h1 className="font-serif text-5xl md:text-7xl font-medium tracking-tighter !leading-tight">
                Dashspace is coming.
              </h1>
              <p className="mt-6 text-lg text-zinc-600 max-w-md">
                A clearer future awaits. Sign up for early access.
              </p>
              <form
                className="flex items-center gap-2 mt-8 max-w-md"
                onSubmit={(e) => e.preventDefault()}
              >
                <Input
                  type="email"
                  placeholder="your@email.com"
                  className="h-11 text-base flex-grow bg-white border-zinc-300 focus-visible:ring-zinc-900"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Email for early access"
                />
                <Button
                  type="submit"
                  size="lg"
                  className="h-11 text-base bg-zinc-900 text-white hover:bg-zinc-800"
                >
                  Get Access
                </Button>
              </form>
            </div>
            <div className="hidden md:flex justify-center items-center h-96 bg-zinc-100 rounded-2xl"></div>
          </section>
          <hr className="my-20 md:my-28 border-zinc-200" />

          {/* The Story Section */}
          <section className="max-w-2xl mx-auto">
            <div className="font-serif text-xl text-zinc-800 leading-relaxed text-left space-y-10">
              <p>
                Every business faces screens filled with noise—
                <br />
                dashboards crowded with complexity,
                <br />
                numbers scattered without clear meaning,
                <br />
                priorities lost in endless distractions.
              </p>
              <p>
                We’ve all experienced it:
                <br />
                Decisions slowed by confusion,
                <br />
                teams pulled in different directions,
                <br />
                clarity slipping further out of reach.
              </p>
              <p>
                At Dashspace, we paused and reflected:
                <br />
                <strong>
                  What if software could genuinely understand your business—
                </strong>
                <br />
                Not just data, but intentions.
                <br />
                Not just processes, but priorities.
                <br />
                Not just tasks, but vision.
              </p>
              <p>
                We imagine software evolving beyond static screens,
                <br />
                into intelligent spaces that adapt intuitively—
                <br />
                highlighting what’s essential,
                <br />
                hiding what’s distracting,
                <br />
                always responding gracefully to changing needs.
              </p>
              <p>
                Dashspace was created to provide exactly that:
                <br />a workspace that listens closely,
                <br />
                reshapes itself intelligently,
                <br />
                and gently guides each business toward clarity,
                <br />
                no matter how quickly the world changes.
              </p>
              <p>
                We believe every business deserves this intelligent space:
                <br />
                <strong>Space to see clearly.</strong>
                <br />
                <strong>Space to decide confidently.</strong>
                <br />
                <strong>Space to innovate naturally.</strong>
                <br />
                <strong>Space to act meaningfully.</strong>
              </p>
              <p>
                That’s why Dashspace exists—
                <br />
                to give every business space to thrive.
              </p>
              <p>
                Dashspace is almost ready.
                <br />A clearer future awaits.
              </p>
              <p>
                <strong>
                  Isn’t it time every business had space to focus?
                </strong>
              </p>
            </div>
          </section>

          {/* Final CTA */}
          <section className="mt-20 md:mt-28 text-center">
            <a
              href="/"
              className="group inline-flex items-center justify-center px-8 py-4 bg-zinc-900 text-white text-lg font-medium rounded-full hover:bg-zinc-800 transition-colors"
            >
              Request Early Access
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </a>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-10 text-sm text-zinc-500">
          <hr className="border-zinc-200" />
          <div className="flex justify-between items-center py-6">
            <DashspaceLogo />
            <div className="flex items-center gap-6">
              <a href="/" className="hover:text-zinc-900">
                X.com
              </a>
              <a href="/" className="hover:text-zinc-900">
                Contact
              </a>
              <a href="/" className="hover:text-zinc-900">
                Careers
              </a>
            </div>
          </div>
          <hr className="border-zinc-200" />
          <div className="flex justify-between items-center pt-6">
            <p>&copy; 2025 Dashspace</p>
            <div className="flex items-center gap-6">
              <a href="/" className="hover:text-zinc-900">
                Terms
              </a>
              <a href="/" className="hover:text-zinc-900">
                Privacy
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
